import { useSelector } from "react-redux"

const AREA_ORDER = ["librarian", "analysis", "lims", "project", "admin"]

const AREA_TITLES = new Map([
  ["librarian", "Librarian"],
  ["analysis", "Analysis"],
  ["lims", "LIMS"],
  ["project", "Projects"],
  ["admin", "Administration"],
])

export function areaTitle(area) {
  return AREA_TITLES.get(area) || area
}

let sitemapPromise = null
const pageCache = new Map()

export function loadSitemap() {
  if (!sitemapPromise) {
    sitemapPromise = fetch("/help/sitemap.json").then((r) => {
      if (!r.ok) throw new Error(`Failed to load sitemap (${r.status})`)
      return r.json()
    })
  }
  return sitemapPromise
}

export function loadPage(area, slug) {
  const key = `${area}/${slug}`
  if (pageCache.has(key)) return Promise.resolve(pageCache.get(key))
  return fetch(`/help/${encodeURIComponent(area)}/${encodeURIComponent(slug)}.md`).then((r) => {
    if (!r.ok) throw new Error(`Failed to load page ${key} (${r.status})`)
    return r.text().then((md) => {
      const stripped = stripFrontmatter(md)
      pageCache.set(key, stripped)
      return stripped
    })
  })
}

function stripFrontmatter(md) {
  return md.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/, "")
}

export function rewriteImagePath(src, area, _slug) {
  if (!src) return src
  if (/^(?:https?:|data:|blob:|\/)/i.test(src)) return src
  // Markdown writes images as ./images/foo.png relative to the .md file at
  // public/help/<area>/intro.md. The actual files now live at
  // public/help/<area>/images/foo.png so the URL only needs the area prefix.
  // This layout also matches what VS Code's markdown preview resolves on disk.
  const cleaned = src.replace(/^\.\//, "")
  return `/help/${encodeURIComponent(area)}/${cleaned}`
}

export function deriveAvailableAreas(authState, teamStatusState) {
  const role = authState?.role
  const isOnlyLibrarian = Boolean(authState?.isOnlyLibrarian)
  const onlyLibrarian = Boolean(teamStatusState?.onlyLibrarian)
  const showLibrarian = Boolean(teamStatusState?.showLibrarian)
  const showAnalyses = Boolean(teamStatusState?.showAnalyses)
  const showLims = Boolean(teamStatusState?.showLims)
  const showProjects = Boolean(teamStatusState?.showProjects)

  if (role === "admin" || role === "customer_admin") return ["admin"]
  if (isOnlyLibrarian || onlyLibrarian) return ["librarian"]

  const areas = []
  if (showLibrarian) areas.push("librarian")
  if (showAnalyses) areas.push("analysis")
  // The LIMS QC module is opt-in via teamStatus.showLims (persisted on the team and read
  // straight from /team-status — the old ptLimsDevFlag dev override is gone), so the LIMS
  // help line shows exactly when the module itself is enabled — mirroring the showLims gate
  // used by the LimsScreen route and the in-app LIMS surfaces.
  if (showLims) areas.push("lims")
  if (showProjects) areas.push("project")
  return areas
}

export function useAvailableAreas() {
  return useSelector((state) =>
    deriveAvailableAreas(state.auth, state.teamStatus)
  )
}

export function groupSitemapByArea(sitemap, availableAreas) {
  const groups = new Map()
  for (const area of availableAreas) groups.set(area, [])
  for (const entry of sitemap) {
    if (groups.has(entry.area)) groups.get(entry.area).push(entry)
  }
  return AREA_ORDER.filter((a) => groups.has(a)).map((area) => ({
    area,
    title: areaTitle(area),
    pages: groups.get(area),
  }))
}

export function findFirstPage(sitemap, area) {
  return sitemap.find((entry) => entry.area === area) || null
}

const IMAGE_PATTERN = /!\[[^\]]{0,500}\]\(([^)]{1,500})\)/g

// Walks the markdown's image references in source order. Each local image
// (relative path like `./images/foo.png`) is replaced inline with a
// `[[IMG:N]]` placeholder whose index points into the deduplicated `paths`
// list. External references (http(s)/data/blob/absolute) are left as-is so
// they survive whatever markdown-stripping happens downstream.
function rewriteImageRefs(md) {
  const paths = []
  const body = md.replace(IMAGE_PATTERN, (match, src) => {
    const raw = (src.trim().split(/\s+/)[0] || "")
    if (!raw) return match
    if (/^(?:https?:|data:|blob:|\/)/i.test(raw)) return match
    const normalized = raw.replace(/^\.\//, "")
    let idx = paths.indexOf(normalized)
    if (idx === -1) {
      idx = paths.length
      paths.push(normalized)
    }
    return `[[IMG:${idx}]]`
  })
  return { body, paths }
}

// Gathers everything Librarian-Processing needs to re-embed the help corpus:
// for each sitemap page, a markdown body where every local image has been
// swapped for a positional `[[IMG:N]]` placeholder, plus a parallel `images`
// list whose index matches the placeholder. The accompanying `imageBlobs`
// map is keyed by `<area>/<imagePath>` so the caller can upload each blob
// to that path under the repo's S3 prefix, and Librarian-Processing reads
// them back at the same key when substituting placeholders with vision
// descriptions inline.
export async function getHelpPagesPayload() {
  const sitemap = await loadSitemap()
  const imageBlobs = {}
  const pages = []

  for (const entry of sitemap) {
    const md = await loadPage(entry.area, entry.slug)
    const { body, paths } = rewriteImageRefs(md)

    await Promise.all(
      paths.map(async (imagePath) => {
        const key = `${entry.area}/${imagePath}`
        if (imageBlobs[key]) return
        const url = `/help/${encodeURIComponent(entry.area)}/${imagePath}`
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`Failed to fetch help image ${url} (${response.status})`)
        }
        imageBlobs[key] = await response.blob()
      })
    )

    pages.push({
      area: entry.area,
      slug: entry.slug,
      title: entry.title,
      body,
      images: paths,
    })
  }

  return { pages, imageBlobs }
}
